import React, { useCallback, useEffect, useState } from "react";
import { FaRobot } from "react-icons/fa";
import { SectionCard, MiniStat } from "../SectionCard";
import { useInView } from "../useInView";
import { fetchOrgLangfuse } from "../api";
import { fmtN, fmtMoney } from "../format";

const ACCENT = "#db2777";

export default function AiCostSection({ organizationId, env, days, onMetrics }) {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchOrgLangfuse(organizationId, env, days);
      const s = json?.summary || {};
      setSummary(s);
      onMetrics?.({ aiCost: s.totalCost, aiCalls: s.traceCount });
    } catch (err) {
      setError(err.message || "Failed to load AI usage analytics");
    } finally {
      setLoading(false);
    }
  }, [organizationId, env, days, onMetrics]);

  useEffect(() => {
    if (inView) load();
  }, [inView, load]);

  const traceCount = summary?.traceCount || 0;
  const totalCost = summary?.totalCost || 0;
  const avgCost = traceCount ? totalCost / traceCount : 0;
  const errorRate = summary?.errorRate || 0;

  return (
    <SectionCard
      ref={ref}
      title="AI Usage & Cost"
      subtitle={`LLM calls and spend over the last ${days} days`}
      icon={<FaRobot />}
      accent={ACCENT}
      loading={(loading || !inView) && !summary}
      error={error}
      onRetry={load}
      size="wide"
      link={{
        to: `/langfuse-analytics?org=${encodeURIComponent(organizationId)}&env=${env}`,
        label: "Open AI Usage Analytics",
      }}
    >
      {summary ? (
        <>
          <div className="oo-ministat-row">
            <MiniStat label="AI calls" value={fmtN(traceCount)} accent={ACCENT} />
            <MiniStat label="Total cost" value={fmtMoney(totalCost)} />
            <MiniStat label="Avg / call" value={fmtMoney(avgCost)} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <span className={`oo-chip ${errorRate > 0.05 ? "oo-chip--danger" : "oo-chip--ok"}`}>
              {(errorRate * 100).toFixed(1)}% errors
            </span>
            {summary.totalPages ? (
              <span className="oo-chip">{fmtN(summary.totalPages)} pages</span>
            ) : null}
          </div>
        </>
      ) : (
        <div className="oo-empty-hint">No AI traces for this org in the period.</div>
      )}
    </SectionCard>
  );
}
